import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ReceiptViewGateway from '../../../gateway/Receipt/ReceiptViewGateway'
import { ReceiptView } from '../../../domain/Receipt/ReceiptView'
import { IntegrationStateHistoryInterface } from '../../../domain/IntegrationStateHistory/IntegrationStateHistory'
import GetIntegrationStateHistoryUseCase from '../../../useCase/Folder/GetIntegrationStateHistoryUseCase'
import IntegrationStateHistoryGateway from '../../../gateway/Folder/IntegrationStateHistoryGateway'
import { Tab, Tabs } from '../tab/Tab'
import IntegrationStateHistoryBlock from '../integrationState/IntegrationStateHistoryBlock'
import ReceiptsValidation from '../integrationState/ReceiptsValidation'

// Styles
import '../../../assets/styles/components/_validation.scss'
import RetrieveCustomerInfoRecallUseCase from '../../../useCase/Folder/RetrieveCustomerInfoRecallUseCase'
import CustomerInfoRecallGateway from '../../../gateway/Folder/CustomerInfoRecallGateway'
import Pairing from '../integrationState/Pairing'
import PairingInterface from '../../../domain/Folder/PairingInterface'
import ViewDossierGateway from '../../../gateway/ViewDossier/ViewDossierGateway'
import RetrieveViewDossierUseCase from '../../../useCase/Folder/RetrieveViewDossierUseCase'
import PresentationDossier from '../integrationState/PresentationDossier'
import { ViewPageInterface } from '../../../domain/ViewDossier/ViewPage'
import FetchFolderGateway from '../../../gateway/Folder/FetchFolderGateway'
import { SignedDocument } from '../../../domain/Docusign/SignedDocument'
import GetListSignedDocumentUseCase from '../../../useCase/Docusign/GetListSignedDocumentUseCase'
import FetchSignedDocumentGateway from '../../../gateway/Docusign/FetchSignedDocumentGateway'
import { FolderDetail } from '../../../domain/Folder/Folder'
import { SubscriberScopeEnum } from '../../../domain/CustomerInfoRecall/SubscriberScopeEnum'
import GetFolderPairingUseCase from '../../../useCase/Folder/GetFolderPairingUseCase'
import SearchCustomerInfoRecallUseCase from '../../../useCase/Folder/SearchCustomerInfoRecallUseCase'
import { PotentialCustomerInterface, SubscriberInterface } from '../../../domain/CustomerInfoRecall/CustomerInfoRecallGatewayInterface'
import FooterValidation from '../integrationState/FooterValidation'
import InvalidElement from '../../../domain/Folder/InvalidElementInterface'
import ErrorModal from '../modal/ErrorModal'
import FolderDetailsValidation from '../folder/FolderComponent/FolderDetailsValidation'

interface Props {
  dossierId: string
}

const BlockValidationSummaryComponent = ({ dossierId }: Props): JSX.Element => {
  const { t } = useTranslation()

  const [dossierSummary, setDossierSummary] = useState<FolderDetail | null>(null)
  const [documents, setDocuments] = useState<SignedDocument[] | null>([])
  const [receipts, setReceipts] = useState<{ [key: string]: ReceiptView[] } | null>(null)
  const [integrationStateHistory, setIntegrationStateHistory] = useState<IntegrationStateHistoryInterface[] | null>(null)
  const [subscribers, setSubscribers] = useState<{ [scope: string]: SubscriberInterface } | null>(null)
  const [potentialCustomers, setPotentialCustomers] = useState<{ [scope: string]: PotentialCustomerInterface[] } | null>(null)
  const [dossierPresentation, setDossierPresentation] = useState<ViewPageInterface[] | null>(null)
  const [state, setState] = useState<string | null>(null)
  const [folderState, setFolderState] = useState<string | null>(null)
  const [pairing, setPairing] = useState<PairingInterface | null>(null)
  const [manualSelection, setManualSelection] = useState<{ [scope: string]: PotentialCustomerInterface | null }>({ np: null, usu: null })
  const [selectedScope, setSelectedScope] = useState<string>(SubscriberScopeEnum.NP)

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [invalidElements, setInvalidElements] = useState<InvalidElement[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    new FetchFolderGateway().getSummary(dossierId).then((response) => {
      setDossierSummary(response)
    })

    new FetchFolderGateway().retrieveState(dossierId).then((response) => {
      const state = t(`common.${response}`)
      setState(state)
      setFolderState(response)
    })

    const pairingUseCase = new GetFolderPairingUseCase(new FetchFolderGateway())
    pairingUseCase.handle(dossierId).then((response) => {
      if (response) {
        setPairing(response)
      }
    })

    new ReceiptViewGateway().retrieveList(dossierId).then((response) => {
      setReceipts(response)
    })

    new GetListSignedDocumentUseCase(new FetchSignedDocumentGateway()).execute(dossierId).then((response) => {
      setDocuments(response)
    })

    const getIntegrationStateHistoryUseCase = new GetIntegrationStateHistoryUseCase(new IntegrationStateHistoryGateway())
    getIntegrationStateHistoryUseCase.handle({ dossierId: dossierId }).then((response) => {
      setIntegrationStateHistory(response)
    })

    const retrieveCustomerInfoRecallUseCase = new RetrieveCustomerInfoRecallUseCase(new CustomerInfoRecallGateway())
    retrieveCustomerInfoRecallUseCase.handle({ dossierId: dossierId, scope: SubscriberScopeEnum.NP }).then((response) => {
      if (response !== null) {
        setSubscribers(response.subscribers)
        setPotentialCustomers(response.customers)
      }
    })

    const retrieveViewDossierUseCase = new RetrieveViewDossierUseCase(new ViewDossierGateway())
    retrieveViewDossierUseCase.handle({ dossierId: dossierId }).then((response) => {
      if (response !== null) {
        setDossierPresentation(response)
      }
    })

  }, [dossierId])

  useEffect(() => {
    [SubscriberScopeEnum.NP, SubscriberScopeEnum.USU].forEach((scope: string) => {
      const pairedUsername = pairing?.pairing[scope] ?? 'undefined';
      if ('undefined' !== pairedUsername) {
        const listedUser = potentialCustomers?.[scope]?.filter(customer => customer.username === pairedUsername);
        if (listedUser?.length === 0 || 'undefined' === typeof listedUser) {
          const searchCustomerInfoRecallUseCase = new SearchCustomerInfoRecallUseCase(new CustomerInfoRecallGateway())
          searchCustomerInfoRecallUseCase.handle(pairedUsername).then((response) => {
            if (response) {
              const newUserSelection = {}
              newUserSelection[scope] = response
              setManualSelection(manualSelection => ({ ...manualSelection, ...newUserSelection }))
            }
          })
        } else {
          const newUserSelection = {}
          newUserSelection[scope] = null
          setManualSelection(manualSelection => ({ ...manualSelection, ...newUserSelection }))
        }
      } else {
        if (!pairing?.pairing[scope]) {
          const newUserSelection = {}
          newUserSelection[scope] = null
          setManualSelection(manualSelection => ({ ...manualSelection, ...newUserSelection }))
        }
      }
    })
  }, [pairing, potentialCustomers])

  const reloadHistory = (scope: string, username: string | null) => {
    const newPairing = {}
    newPairing[scope] = username
    setPairing(prev => {
      if (!prev) {
        return { pairing: { ...newPairing }, forceMandate: false }
      }
      return { ...prev, pairing: { ...prev.pairing, ...newPairing } }
    })
    const getIntegrationStateHistoryUseCase = new GetIntegrationStateHistoryUseCase(new IntegrationStateHistoryGateway())
    getIntegrationStateHistoryUseCase.handle({ dossierId: dossierId }).then((response) => {
      setIntegrationStateHistory(response)
    })
  };

  const updateAllReceiptsState = async (state: string, reason: string | null) => {
    setIsLoading(true)
    const folderGateway = new FetchFolderGateway()
    const folderDetails = await folderGateway.getSummary(dossierId).then((response) => { return response })
    if (folderDetails) {
      if ('mandate' === folderDetails.detention_mode) {
        const pairingUseCase = new GetFolderPairingUseCase(new FetchFolderGateway())
        const pairingConfig = await pairingUseCase.handle(dossierId).then((response) => { return response })
        const pairing = pairingConfig?.pairing
        if (!pairingConfig || pairingConfig.forceMandate) {
          if (folderDetails.dismemberment_scope === SubscriberScopeEnum.NP) {
            if (!pairing || !pairing.usu) {
              setErrorMessage(t('validation.error'));
              return
            }
          } else {
            if (!pairing || !pairing.np) {
              setErrorMessage(t('validation.error'));
              return
            }
          }
        }
      }
    }

    folderGateway.updateDossierIntegrationState(dossierId, state, reason, invalidElements).then(response => {
      if (response) {
        window.location.href = `/manage-dossier/statut-integration/ongoing`
      } else {
        setErrorMessage(t('error.error-occured'))
      }
    }).finally(() =>
      setIsLoading(false)
    )
  }

  const findIndexByCombinedId = (elements: InvalidElement[], targetElementId: string, targetReceiptId: string | null): number => {
    return elements.findIndex(
      (element) => element.elementId === targetElementId && element.receiptId === targetReceiptId
    );
  };

  const handleInvalidateToggle = (isInvalid: boolean, elementId: string, comment = "", blockId: string, receiptId: string | null, value: string | null, pageId: string) => {
    setInvalidElements(prevElements => {
      const index = findIndexByCombinedId(prevElements, elementId, receiptId);

      if (isInvalid) {
        if (index === -1) {
          return [...prevElements, { comment, elementId, blockId, receiptId, value, pageId }];
        }
        else {
          return prevElements.map((doc, i) =>
            i === index ? { ...doc, comment } : doc
          );
        }
      } else {
        return prevElements.filter((_, i) => i !== index);
      }
    });
  };

  const handleCommentChange = (elementId: string, receiptId: string | null, newComment: string) => {
    setInvalidElements(prevElements => {
      const index = findIndexByCombinedId(prevElements, elementId, receiptId);
      if (index === -1) return prevElements;

      return prevElements.map((doc, i) =>
        i === index ? { ...doc, comment: newComment } : doc
      );
    });
  };

  const count = Object.keys(invalidElements).length;
  const isAllValid = count === 0;
  const mainBtnText = isAllValid ? t('button.validate-all') : t('button.ask-new-corrections');

  return (
    <>
      {errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage(null)} />}
      <div className="box-elevations box-elevations--base box-elevations--mb u-mxAuto u-pxm u-pym">
        <h4 className="u-mb0">
          {dossierSummary && <FolderDetailsValidation FolderDetail={dossierSummary} error={false} FolderStatuses={state ? [state] : []} />}
        </h4>
      </div>
      <Tabs classes="wrap u-mxAuto u-pbb">
        {dossierPresentation !== null &&
          <Tab key="folder" label={t('validation.presentation-dossier')}>
            <PresentationDossier
              state={folderState}
              viewPages={dossierPresentation}
              onInvalidateToggle={handleInvalidateToggle}
              onCommentChange={handleCommentChange}
              invalidElements={invalidElements}
            />
          </Tab>
        }
        {receipts !== null &&
          <Tab key="receipt" label={t('validation.receipts')}>
            <ReceiptsValidation
              dossierId={dossierId}
              state={folderState}
              receipts={receipts}
              documents={documents}
              onInvalidateToggle={handleInvalidateToggle}
              onCommentChange={handleCommentChange}
              invalidElements={invalidElements}
            />
          </Tab>
        }
        {subscribers !== null && (folderState === 'ongoing' || folderState === 'error') &&
          <Tab key="pairing" label={t('validation.pairing')}>
            <Pairing
              dossierId={dossierId}
              dossierSummary={dossierSummary}
              subscribers={subscribers}
              potentialCustomers={potentialCustomers}
              setScope={setSelectedScope}
              scope={selectedScope}
              pairing={pairing}
              manualSelection={manualSelection}
              onModify={reloadHistory}
            />
          </Tab>
        }
        {integrationStateHistory !== null &&
          <Tab key="history" label={t('validation.actions')}>
            <IntegrationStateHistoryBlock integrationStateHistory={integrationStateHistory} />
          </Tab>
        }
      </Tabs>

      {("ongoing" === folderState || "error" === folderState) &&
        <FooterValidation
          isAllValid={isAllValid}
          count={count}
          mainBtnText={mainBtnText}
          updateAllReceiptsState={updateAllReceiptsState}
          isLoading={isLoading}
        />
      }
    </>
  )
}

export default BlockValidationSummaryComponent
